import { ChangeEvent, ReactNode, useMemo, useState } from 'react';
import { Box, Flex, Select, SimpleGrid, Text } from '@chakra-ui/react';
import { ApexOptions } from 'apexcharts';
import Chart from 'react-apexcharts';

import { IKmEntry, formatKm, sumKmBy } from './kmData';

interface IKmChartsProps {
  entries: IKmEntry[];
  dateFrom: string;
  dateTo: string;
}

const MONTHS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

// Até ~2 meses de período mostra por dia; acima disso, por mês
const MAX_DAYS_BY_DAY = 62;

const TOP_LIMIT = 10;

const baseOptions: ApexOptions = {
  chart: {
    background: 'transparent',
    foreColor: '#A0AEC0',
    toolbar: { show: false },
    fontFamily: 'inherit',
  },
  theme: { mode: 'dark' },
  colors: ['#3182CE'],
  dataLabels: { enabled: false },
  grid: { borderColor: 'rgba(255, 255, 255, 0.08)' },
  tooltip: {
    theme: 'dark',
    y: { formatter: (value: number) => formatKm(value) },
  },
};

const daysBetween = (from: string, to: string): number =>
  Math.round(
    (new Date(`${to}T12:00:00`).getTime() -
      new Date(`${from}T12:00:00`).getTime()) /
      86400000,
  );

function ChartCard({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Box
      p="4"
      bg="whiteAlpha.50"
      borderRadius="md"
      border="1px solid"
      borderColor="whiteAlpha.200"
    >
      <Flex justify="space-between" align="center" mb="2" minH="32px">
        <Text fontSize="sm" fontWeight="bold" color="gray.300">
          {title}
        </Text>
        {action}
      </Flex>
      {children}
    </Box>
  );
}

function EmptyChart() {
  return (
    <Flex h="260px" align="center" justify="center">
      <Text fontSize="sm" color="gray.500">
        Nenhum KM no período e filtros selecionados.
      </Text>
    </Flex>
  );
}

export function KmCharts({ entries, dateFrom, dateTo }: IKmChartsProps) {
  const [groupBy, setGroupBy] = useState<'costCenter' | 'section'>(
    'costCenter',
  );

  // KM ao longo do tempo (por dia ou por mês, conforme o tamanho do período)
  const timeline = useMemo(() => {
    const dates = entries
      .map(e => e.date)
      .filter(Boolean)
      .sort();
    const from = dateFrom || dates[0] || '';
    const to = dateTo || dates[dates.length - 1] || '';
    const byDay = !!from && !!to && daysBetween(from, to) <= MAX_DAYS_BY_DAY;

    const totals = new Map<string, number>();
    entries.forEach(entry => {
      const key = byDay ? entry.date : entry.date.substring(0, 7);
      totals.set(key, (totals.get(key) || 0) + entry.km);
    });

    const keys = Array.from(totals.keys()).sort();
    const labels = keys.map(key => {
      const [year, month, day] = key.split('-');
      return byDay
        ? `${day}/${month}`
        : `${MONTHS[Number(month) - 1]}/${year.substring(2)}`;
    });

    return {
      byDay,
      labels,
      values: keys.map(key => Math.round((totals.get(key) || 0) * 10) / 10),
    };
  }, [entries, dateFrom, dateTo]);

  const handleGroupByChange = (e: ChangeEvent<HTMLSelectElement>) =>
    setGroupBy(e.target.value as 'costCenter' | 'section');

  const topUsers = useMemo(
    () => sumKmBy(entries, e => e.userName).slice(0, TOP_LIMIT),
    [entries],
  );

  const topGroups = useMemo(
    () =>
      sumKmBy(entries, e =>
        groupBy === 'costCenter' ? e.costCenterLabel : e.sectionLabel,
      ).slice(0, TOP_LIMIT),
    [entries, groupBy],
  );

  const horizontalOptions = (labels: string[]): ApexOptions => ({
    ...baseOptions,
    plotOptions: { bar: { horizontal: true, borderRadius: 3 } },
    xaxis: {
      categories: labels,
      labels: { formatter: (value: string) => formatKm(Number(value)) },
    },
    yaxis: { labels: { maxWidth: 220 } },
  });

  const horizontalHeight = (count: number) => Math.max(260, count * 34 + 60);

  const groupBySelect = (
    <Select
      size="xs"
      w="150px"
      value={groupBy}
      onChange={handleGroupByChange}
      bg="whiteAlpha.100"
      borderColor="whiteAlpha.300"
      color="gray.200"
    >
      <option
        value="costCenter"
        style={{ background: '#fff', color: '#1a202c' }}
      >
        Centro de custo
      </option>
      <option value="section" style={{ background: '#fff', color: '#1a202c' }}>
        Seção
      </option>
    </Select>
  );

  return (
    <Box mb="6">
      <Box mb="4">
        <ChartCard title={timeline.byDay ? 'KM por dia' : 'KM por mês'}>
          {entries.length === 0 ? (
            <EmptyChart />
          ) : (
            <Chart
              type="bar"
              height={280}
              series={[{ name: 'KM', data: timeline.values }]}
              options={{
                ...baseOptions,
                plotOptions: { bar: { borderRadius: 3, columnWidth: '60%' } },
                xaxis: { categories: timeline.labels },
                yaxis: {
                  labels: { formatter: (value: number) => formatKm(value) },
                },
              }}
            />
          )}
        </ChartCard>
      </Box>

      <SimpleGrid columns={[1, 1, 2]} spacing="4">
        <ChartCard title={`Usuários que mais rodaram (top ${TOP_LIMIT})`}>
          {topUsers.length === 0 ? (
            <EmptyChart />
          ) : (
            <Chart
              type="bar"
              height={horizontalHeight(topUsers.length)}
              series={[{ name: 'KM', data: topUsers.map(g => g.km) }]}
              options={horizontalOptions(topUsers.map(g => g.label))}
            />
          )}
        </ChartCard>

        <ChartCard
          title={`KM por ${
            groupBy === 'costCenter' ? 'centro de custo' : 'seção'
          } (top ${TOP_LIMIT})`}
          action={groupBySelect}
        >
          {topGroups.length === 0 ? (
            <EmptyChart />
          ) : (
            <Chart
              type="bar"
              height={horizontalHeight(topGroups.length)}
              series={[{ name: 'KM', data: topGroups.map(g => g.km) }]}
              options={horizontalOptions(topGroups.map(g => g.label))}
            />
          )}
        </ChartCard>
      </SimpleGrid>
    </Box>
  );
}
